import React, { useState, useRef, useCallback, useEffect } from "react";
import MetricTooltip from "./MetricTooltip";
import { METRIC_META } from "../../constants/metricMeta";
import type { MetricSample } from "../../hooks/useMetricSamples";

// =============================================================================
// METRIC INFO ICON
// =============================================================================
// PURPOSE: Interactive info icon (ⓘ) that triggers MetricTooltip on hover.
//
// REQUIREMENTS:
// - Hover delay (300ms) before showing tooltip
// - Portal-based tooltip rendering for z-index control
// - Auto-positions tooltip above/below based on viewport space
// - Fetches metric samples on hover
// =============================================================================

interface MetricInfoIconProps {
  metricKey: string;
  getSamples: (key: string) => MetricSample[];
  children?: React.ReactNode;
}

const MetricInfoIcon: React.FC<MetricInfoIconProps> = ({ metricKey, getSamples, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [samples, setSamples] = useState<MetricSample[]>([]);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const iconRef = useRef<HTMLSpanElement>(null);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const closeTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  const meta = METRIC_META[metricKey];

  const open = useCallback(() => {
    clearTimeout(closeTimeout.current);
    if (iconRef.current) {
      setAnchorRect(iconRef.current.getBoundingClientRect());
    }
    setSamples(getSamples(metricKey));
    setIsOpen(true);
  }, [metricKey, getSamples]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const scheduleClose = useCallback(() => {
    clearTimeout(hoverTimeout.current);
    closeTimeout.current = setTimeout(close, 250);
  }, [close]);

  // Close on click outside (check both icon and portal tooltip)
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (iconRef.current && iconRef.current.contains(target)) return;
      // Check if click is inside the portal-rendered tooltip
      const tooltipEl = document.querySelector(`[data-tooltip-key="${metricKey}"]`);
      if (tooltipEl && tooltipEl.contains(target)) return;
      close();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, close, metricKey]);

  // Close on scroll (but not scrolls inside the tooltip itself)
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: Event) => {
      const tooltipEl = document.querySelector(`[data-tooltip-key="${metricKey}"]`);
      if (tooltipEl && tooltipEl.contains(e.target as Node)) return;
      close();
    };
    window.addEventListener("scroll", handler, true);
    return () => window.removeEventListener("scroll", handler, true);
  }, [isOpen, close, metricKey]);

  if (!meta) return null;

  const handleMouseEnter = () => {
    clearTimeout(closeTimeout.current);
    hoverTimeout.current = setTimeout(open, 200);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeout.current);
    scheduleClose();
  };

  const handleTooltipMouseEnter = () => {
    clearTimeout(closeTimeout.current);
    clearTimeout(hoverTimeout.current);
  };

  const handleTooltipMouseLeave = () => {
    scheduleClose();
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOpen) close();
    else open();
  };

  return (
    <>
      <span
        ref={iconRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        className="cursor-help"
        aria-label={`Info: ${meta.formula}`}
      >
        {children}
      </span>
      {isOpen && anchorRect && (
        <MetricTooltip
          meta={meta}
          samples={samples}
          anchorRect={anchorRect}
          tooltipKey={metricKey}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        />
      )}
    </>
  );
};

export default MetricInfoIcon;
